import { useNavigate } from 'react-router-dom'
import {
  Plus,
  FileText,
  BarChart3,
  Truck,
  ChevronRight,
  Lock,
} from 'lucide-react'

export default function Home() {
  const navigate = useNavigate()

  return (
    <div className="min-h-dvh flex flex-col bg-white safe-top safe-bottom">
      {/* Header with logo */}
      <header className="px-6 pt-8 pb-6 animate-fade-in">
        <img
          src="/yi-logo.png"
          alt="Yield Innovations"
          className="h-10 w-auto"
        />
      </header>

      {/* Hero Section */}
      <div className="px-6 pb-8">
        <h1 className="text-[1.5rem] leading-tight font-bold text-yi-green-dark mb-4 animate-slide-up opacity-0 delay-1 tracking-wide">
          Product{' '}
          <span className="gold-underline">Recommender</span>
        </h1>
        <p className="text-[15px] text-gray-500 leading-relaxed animate-slide-up opacity-0 delay-2 font-normal">
          Build personalized recommendations based on farm setup,
          soil conditions, and agronomic goals.
        </p>
      </div>

      {/* Main Action Card */}
      <div className="px-6 pb-6 animate-slide-up opacity-0 delay-3">
        <button
          onClick={() => navigate('/questionnaire')}
          className="group w-full bg-yi-green hover:bg-yi-green-dark text-white
                     rounded-2xl p-5 text-left btn-press relative overflow-hidden
                     shadow-[0_8px_30px_-12px_rgba(26,71,42,0.4)]
                     hover:shadow-[0_12px_40px_-12px_rgba(26,71,42,0.5)]
                     transition-all duration-300"
        >
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />

          <div className="relative flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm
                            flex items-center justify-center
                            group-hover:bg-white/15 transition-colors">
              <Plus className="w-6 h-6" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-lg mb-0.5">New Recommendation</p>
              <p className="text-sm text-white/70">Start a customer session</p>
            </div>
            <ChevronRight className="w-5 h-5 text-white/50 group-hover:text-white/80
                                      group-hover:translate-x-1 transition-all" />
          </div>
        </button>
      </div>

      {/* Divider with label */}
      <div className="px-6 py-2 animate-fade-in opacity-0 delay-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-gray-100" />
          <span className="text-[11px] font-medium text-gray-300 uppercase tracking-widest">
            Coming Soon
          </span>
          <div className="flex-1 h-px bg-gray-100" />
        </div>
      </div>

      {/* Future Features */}
      <div className="px-6 py-4 space-y-1 animate-slide-up opacity-0 delay-5">
        <FeatureRow
          icon={<FileText className="w-[18px] h-[18px]" />}
          title="Saved Recommendations"
          description="Access past customer sessions"
        />
        <FeatureRow
          icon={<BarChart3 className="w-[18px] h-[18px]" />}
          title="Analytics Dashboard"
          description="Track product performance"
        />
        <FeatureRow
          icon={<Truck className="w-[18px] h-[18px]" />}
          title="Order Integration"
          description="Submit orders directly"
        />
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Footer */}
      <footer className="px-6 py-5 border-t border-gray-50">
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-gray-300">
            v1.0
          </p>
          <p className="text-[11px] text-gray-300">
            Built by <span className="text-gray-400">Essential</span>
          </p>
        </div>
      </footer>
    </div>
  )
}

function FeatureRow({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="flex items-center gap-4 p-3.5 rounded-xl
                    hover:bg-gray-50/50 transition-colors cursor-default
                    opacity-50">
      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-600 text-[14px] leading-tight">{title}</p>
        <p className="text-[12px] text-gray-400 mt-0.5">{description}</p>
      </div>
      <Lock className="w-4 h-4 text-gray-300" />
    </div>
  )
}
